// src/pages/Trains.jsx
import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const TYPE_LABELS = { Ex: 'Express', IC: 'Intercity', Ml: 'Mail', Com: 'Commuter' };
const TYPE_COLORS = {
  Ex:  { bg: 'rgba(251,191,36,.12)',  text: '#fbbf24', border: 'rgba(251,191,36,.3)' },
  IC:  { bg: 'rgba(99,102,241,.12)',  text: '#818cf8', border: 'rgba(99,102,241,.3)' },
  Ml:  { bg: 'rgba(34,197,94,.12)',   text: '#4ade80', border: 'rgba(34,197,94,.3)'  },
  Com: { bg: 'rgba(251,146,60,.12)',  text: '#fb923c', border: 'rgba(251,146,60,.3)' },
};

const s = {
  page: { minHeight: '100vh', background: '#0f1117', padding: '0 0 60px', fontFamily: "'DM Sans', sans-serif" },
  header: {
    background: 'linear-gradient(135deg, #0f1117 0%, #1a1f2e 100%)',
    borderBottom: '1px solid #1e2433', padding: '28px 32px 24px',
    position: 'sticky', top: 0, zIndex: 10, backdropFilter: 'blur(12px)',
  },
  headerTop: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 },
  headerIcon: {
    width: 42, height: 42, background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
    borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
  },
  headerTitle: { color: '#f1f5f9', fontSize: 20, fontWeight: 700, letterSpacing: '-.3px' },
  headerSub: { color: '#64748b', fontSize: 13, marginTop: 2 },
  searchRow: { display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 10 },
  searchBox: { flex: 1, minWidth: 200, position: 'relative' },
  searchIcon: { position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569', fontSize: 15, pointerEvents: 'none' },
  input: {
    width: '100%', background: '#1e2433', border: '1px solid #2a3348',
    borderRadius: 8, padding: '10px 12px 10px 36px', color: '#e2e8f0',
    fontSize: 13, outline: 'none', boxSizing: 'border-box',
  },
  routeRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  routeInput: {
    flex: 1, minWidth: 140, background: '#1e2433', border: '1px solid #2a3348',
    borderRadius: 8, padding: '10px 12px', color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box',
  },
  routeArrow: { color: '#3b82f6', fontSize: 18, fontWeight: 700, flexShrink: 0 },
  routeBtn: {
    padding: '10px 18px', background: 'linear-gradient(135deg, #3b82f6, #6366f1)',
    border: 'none', borderRadius: 8, color: '#fff', fontSize: 13,
    fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  },
  clearBtn: { padding: '10px 14px', background: '#1e2433', border: '1px solid #2a3348', borderRadius: 8, color: '#94a3b8', fontSize: 13, cursor: 'pointer' },
  tabRow: { display: 'flex', gap: 8, padding: '16px 32px 0', borderBottom: '1px solid #1e2433' },
  tab: {
    padding: '10px 18px', background: 'none', border: 'none',
    borderBottom: '2px solid transparent', color: '#64748b',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', marginBottom: -1,
  },
  tabActive: { color: '#3b82f6', borderBottom: '2px solid #3b82f6' },
  body: { padding: '24px 32px' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 14 },
  card: {
    background: '#151a26', border: '1px solid #1e2433', borderRadius: 12,
    padding: '16px 18px', cursor: 'pointer', transition: 'all .2s',
    position: 'relative', overflow: 'hidden',
  },
  cardOff: { background: '#13151e', border: '1px solid #2a1a1a', opacity: .75 },
  accent: { position: 'absolute', top: 0, left: 0, width: 3, height: '100%', borderRadius: '12px 0 0 12px', background: 'linear-gradient(180deg, #3b82f6, #6366f1)' },
  accentOff: { background: 'linear-gradient(180deg, #dc2626, #991b1b)' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 },
  trainId: { color: '#475569', fontSize: 11, fontWeight: 700, letterSpacing: '.5px', marginBottom: 3 },
  trainName: { color: '#f1f5f9', fontSize: 15, fontWeight: 700 },
  badge: { padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, border: '1px solid', flexShrink: 0 },
  offBadge: { padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'rgba(220,38,38,.12)', color: '#f87171', border: '1px solid rgba(220,38,38,.3)' },
  routeVis: { display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0' },
  stationName: { color: '#e2e8f0', fontSize: 13, fontWeight: 600 },
  line: { flex: 1, height: 1, background: 'linear-gradient(90deg, #2a3348, #3b82f6, #2a3348)' },
  dot: { width: 6, height: 6, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 },
  timeRow: { display: 'flex', justifyContent: 'space-between', marginTop: 8 },
  timeLabel: { color: '#475569', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' },
  timeVal: { color: '#94a3b8', fontSize: 13, fontWeight: 600, fontFamily: "'DM Mono', monospace" },
  offDayRow: { display: 'flex', gap: 5, marginTop: 10, flexWrap: 'wrap', alignItems: 'center' },
  dayPill: { padding: '2px 8px', borderRadius: 20, fontSize: 10, fontWeight: 700, background: '#1e2433', color: '#475569', border: '1px solid #2a3348' },
  dayPillOff: { background: 'rgba(220,38,38,.12)', color: '#f87171', border: '1px solid rgba(220,38,38,.3)' },
  viewBtn: {
    marginTop: 12, padding: '7px 12px', background: 'rgba(59,130,246,.08)',
    border: '1px solid rgba(59,130,246,.2)', borderRadius: 7,
    color: '#3b82f6', fontSize: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6,
  },
  statsRow: { display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' },
  statPill: { padding: '5px 14px', background: '#1e2433', borderRadius: 20, color: '#64748b', fontSize: 12, fontWeight: 600, border: '1px solid #2a3348' },
  statVal: { color: '#e2e8f0', marginLeft: 6 },
  empty: { gridColumn: '1/-1', textAlign: 'center', padding: '60px 20px', color: '#475569', fontSize: 14 },
  emptyIcon: { fontSize: 40, marginBottom: 12, display: 'block' },
  sectionLabel: { color: '#94a3b8', fontSize: 11, fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 14 },
};

function TrainCard({ train, isOff = false, onClick }) {
  const typeColor = TYPE_COLORS[train.type] || TYPE_COLORS.Ml;
  const hasRoute = !!train.route_details?.routes;
  const todayName = DAY_NAMES[new Date().getDay()];

  return (
    <div
      style={{ ...s.card, ...(isOff ? s.cardOff : {}) }}
      onClick={() => onClick(train)}
      onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(59,130,246,.12)'; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = isOff ? '#2a1a1a' : '#1e2433'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
    >
      <div style={{ ...s.accent, ...(isOff ? s.accentOff : {}) }} />
      <div style={{ paddingLeft: 10 }}>
        <div style={s.cardHeader}>
          <div>
            <div style={s.trainId}>#{train.trainID}</div>
            <div style={s.trainName}>{train.trainName}</div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-end' }}>
            <div style={{ ...s.badge, background: typeColor.bg, color: typeColor.text, borderColor: typeColor.border }}>
              {TYPE_LABELS[train.type] || train.type}
            </div>
            {isOff && <div style={s.offBadge}>আজ বন্ধ</div>}
          </div>
        </div>

        <div style={s.routeVis}>
          <div style={s.dot} />
          <div style={s.stationName}>{train.departStation}</div>
          <div style={s.line} />
          <div style={s.stationName}>{train.arrivalStation}</div>
          <div style={{ ...s.dot, background: '#6366f1' }} />
        </div>

        <div style={s.timeRow}>
          <div><div style={s.timeLabel}>ছাড়ে</div><div style={s.timeVal}>{train.departTime}</div></div>
          <div style={{ textAlign: 'right' }}><div style={s.timeLabel}>পৌঁছায়</div><div style={s.timeVal}>{train.arrivalTime}</div></div>
        </div>

        {train.offDay && train.offDay !== 'N/A' && (
          <div style={s.offDayRow}>
            <span style={{ color: '#475569', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>বন্ধ:</span>
            {train.offDay.split(',').map(d => d.trim()).map(d => (
              <span key={d} style={{ ...s.dayPill, ...(d === todayName ? s.dayPillOff : {}) }}>{d}</span>
            ))}
          </div>
        )}

        <div style={s.viewBtn}>
          🗺️ {hasRoute ? `${train.route_details.routes.length} টি স্টেশন · Route দেখুন →` : 'Route দেখুন →'}
        </div>
      </div>
    </div>
  );
}

export default function AllTrains({ trainsData = [] }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState('all');
  const [query, setQuery] = useState('');
  const [fromStation, setFromStation] = useState('');
  const [toStation, setToStation] = useState('');
  const [routeSearch, setRouteSearch] = useState({ from: '', to: '' });

  const todayName = DAY_NAMES[new Date().getDay()];

  const offToday = useMemo(() =>
    trainsData.filter(t => {
      if (!t.offDay || t.offDay === 'N/A') return false;
      return t.offDay.split(',').map(d => d.trim()).includes(todayName);
    }), [trainsData, todayName]);

  const filtered = useMemo(() => {
    let list = trainsData;
    if (routeSearch.from || routeSearch.to) {
      const from = routeSearch.from.toLowerCase();
      const to = routeSearch.to.toLowerCase();
      return list.filter(t => {
        const stations = [t.departStation, t.arrivalStation, ...(t.route_details?.routes?.map(r => r.city) || [])].map(s => s?.toLowerCase() || '');
        if (from && to) {
          const fi = stations.findIndex(s => s.includes(from));
          const ti = stations.findIndex(s => s.includes(to));
          return fi !== -1 && ti !== -1;
        }
        return (!from || stations.some(s => s.includes(from))) && (!to || stations.some(s => s.includes(to)));
      });
    }
    if (query) {
      const q = query.toLowerCase();
      return list.filter(t =>
        t.trainName?.toLowerCase().includes(q) || String(t.trainID).includes(q) ||
        t.departStation?.toLowerCase().includes(q) || t.arrivalStation?.toLowerCase().includes(q) ||
        t.route_details?.routes?.some(r => r.city?.toLowerCase().includes(q))
      );
    }
    return list;
  }, [trainsData, query, routeSearch]);

  const handleCardClick = (train) => navigate(`/trains/${train.trainID}`, { state: { train } });
  const handleRouteSearch = () => setRouteSearch({ from: fromStation, to: toStation });
  const clearRoute = () => { setFromStation(''); setToStation(''); setRouteSearch({ from: '', to: '' }); };
  const isRouteActive = routeSearch.from || routeSearch.to;

  return (
    <div style={s.page}>
      <link href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700&family=DM+Mono:wght@400;600&display=swap" rel="stylesheet" />

      <div style={s.header}>
        <div style={s.headerTop}>
          <div style={s.headerIcon}>🚂</div>
          <div>
            <div style={s.headerTitle}>Train Directory</div>
            <div style={s.headerSub}>{trainsData.length} টি ট্রেন · আজ {todayName}</div>
          </div>
        </div>
        <div style={s.searchRow}>
          <div style={s.searchBox}>
            <span style={s.searchIcon}>🔍</span>
            <input style={s.input} placeholder="ট্রেনের নাম, নম্বর বা স্টেশন..." value={query} onChange={e => { setQuery(e.target.value); clearRoute(); }} />
          </div>
        </div>
        <div style={s.routeRow}>
          <input style={s.routeInput} placeholder="From স্টেশন..." value={fromStation} onChange={e => setFromStation(e.target.value)} />
          <span style={s.routeArrow}>→</span>
          <input style={s.routeInput} placeholder="To স্টেশন..." value={toStation} onChange={e => setToStation(e.target.value)} />
          <button style={s.routeBtn} onClick={handleRouteSearch}>খুঁজুন</button>
          {isRouteActive && <button style={s.clearBtn} onClick={clearRoute}>✕</button>}
        </div>
      </div>

      <div style={s.tabRow}>
        {[['all', `সব ট্রেন (${trainsData.length})`], ['off', `আজ বন্ধ (${offToday.length})`]].map(([key, label]) => (
          <button key={key} style={{ ...s.tab, ...(tab === key ? s.tabActive : {}) }} onClick={() => setTab(key)}>{label}</button>
        ))}
      </div>

      <div style={s.body}>
        {tab === 'all' && (
          <>
            {isRouteActive
              ? <div style={{ ...s.sectionLabel, color: '#3b82f6', marginBottom: 16 }}>🔍 "{routeSearch.from}" → "{routeSearch.to}" — {filtered.length} টি ট্রেন</div>
              : !query && (
                <div style={s.statsRow}>
                  {Object.entries(TYPE_LABELS).map(([k, v]) => {
                    const count = trainsData.filter(t => t.type === k).length;
                    return count > 0 ? <div key={k} style={s.statPill}>{v}<span style={s.statVal}>{count}</span></div> : null;
                  })}
                </div>
              )
            }
            <div style={s.grid}>
              {filtered.length === 0
                ? <div style={s.empty}><span style={s.emptyIcon}>🔍</span>কোনো ট্রেন পাওয়া যায়নি</div>
                : filtered.map(t => <TrainCard key={t._id} train={t} onClick={handleCardClick} />)
              }
            </div>
          </>
        )}
        {tab === 'off' && (
          <>
            <div style={{ ...s.sectionLabel, marginBottom: 16 }}>আজ {todayName} — বন্ধ থাকা ট্রেন</div>
            <div style={s.grid}>
              {offToday.length === 0
                ? <div style={s.empty}><span style={s.emptyIcon}>✅</span>আজ সব ট্রেন চলছে!</div>
                : offToday.map(t => <TrainCard key={t._id} train={t} isOff onClick={handleCardClick} />)
              }
            </div>
          </>
        )}
      </div>
    </div>
  );
}